import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Calendar, Tag, Heart, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

export default function ProgramDetailPage() {
  const { id } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/programs/${id}`).then(r => setProgram(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 size={32} className="animate-spin text-royal-800" /></div>;
  if (!program) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-gray-500">Program not found.</p>
      <Link to="/programs" className="btn-royal text-sm px-5 py-2.5">Back to Programs</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        <div className="relative bg-hero-gradient h-72 overflow-hidden">
          {program.image && <img src={program.image} alt={program.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-end pb-8">
            <Link to="/programs" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4 transition-colors">
              <ArrowLeft size={16} /> Back to Programs
            </Link>
            <span className={`self-start px-3 py-1 text-xs font-bold rounded-full capitalize mb-3 ${program.status==='active'?'bg-emerald-500 text-white':'bg-gold-500 text-royal-950'}`}>{program.status}</span>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-white">{program.title}</h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
                <h2 className="font-display font-semibold text-gray-900 text-xl mb-4">About This Program</h2>
                <div className="text-gray-600 leading-relaxed whitespace-pre-wrap">{program.description}</div>
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Program Details</h3>
                <div className="space-y-3">
                  {program.category && <div className="flex items-center gap-3 text-sm"><Tag size={16} className="text-royal-800 flex-shrink-0" /><span className="capitalize text-gray-700">{program.category}</span></div>}
                  {program.location && <div className="flex items-center gap-3 text-sm"><MapPin size={16} className="text-royal-800 flex-shrink-0" /><span className="text-gray-700">{program.location}</span></div>}
                  {program.beneficiaries && <div className="flex items-center gap-3 text-sm"><Users size={16} className="text-royal-800 flex-shrink-0" /><span className="text-gray-700">{program.beneficiaries.toLocaleString()}+ beneficiaries</span></div>}
                  {program.startDate && <div className="flex items-center gap-3 text-sm"><Calendar size={16} className="text-royal-800 flex-shrink-0" /><span className="text-gray-700">Started {new Date(program.startDate).toLocaleDateString('en-NG',{month:'long',year:'numeric'})}</span></div>}
                </div>
              </div>
              <div className="bg-gold-gradient rounded-2xl p-6">
                <h3 className="font-display font-bold text-royal-950 mb-2">Support This Program</h3>
                <p className="text-royal-800/70 text-sm mb-4">Your donation goes directly to making this program possible.</p>
                <Link to="/donate" className="w-full btn-royal justify-center block text-center py-3 text-sm">
                  <Heart size={15} className="inline mr-2 fill-current" />Donate Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
